import React, { useMemo, useState } from 'react';
import {
  buildSharePackage, validateSharePackage, shareFilename, HELP_URL,
} from '../utils/share';
import SharePreview from './SharePreview';
import styles from './Share.module.css';

// ============================================================
// ShareExport — "Share to Mesa Robles": download a recipe package
// ============================================================
// The dialog shows the display block as a page, because that is literally
// what Mesa Robles will render. Warnings are shown in amber and never block
// the download: the calculator is the safety check, and the site shows the
// figures exactly as the file states them.
export default function ShareExport({ calc, ingDB, onClose }) {
  const [done, setDone] = useState(false);

  const { pkg, result } = useMemo(() => {
    // Build from the recipe as it stands, saved or not: saveRecipe()'s own
    // field list, so an unsaved recipe shares exactly what it would save.
    const raw = {
      id: calc.currentRecipeId,
      savedAt: null,
      recipeName: calc.recipeName,
      cookMethod: calc.cookMethod,
      unitSystem: calc.unitSystem,
      meatWeight: calc.meatWeight,
      sausageStyle: calc.sausageStyle,
      rating: calc.rating,
      recipeNotes: calc.recipeNotes,
      equipLoss: calc.equipLoss,
      cookLossPct: calc.cookLossPct,
      typicalLinkWeightG: calc.typicalLinkWeightG,
      targetCooked: calc.targetCooked,
      targetUnit: calc.targetUnit,
      targetLocked: calc.targetLocked,
      selectedCasing: calc.selectedCasing,
      linkLength: calc.linkLength,
      linkWeight: calc.linkWeight,
      userFtPerLb: calc.userFtPerLb,
      rows: calc.rows,
      methodSections: calc.methodSections,
    };
    try {
      const built = buildSharePackage(raw, { ingDB });
      return { pkg: built, result: validateSharePackage(built) };
    } catch (e) {
      return { pkg: null, result: { ok: false, errors: [`Could not build the file: ${e.message}`], warnings: [], pkg: null } };
    }
  }, [calc, ingDB]);

  const nothingToShare = (calc.filledIngredients?.length || 0) === 0;
  const filename = pkg ? shareFilename(pkg) : '';

  const handleDownload = () => {
    if (!pkg || !result.ok) return;
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setDone(true);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <div className={styles.header}>
          <div>
            <div className={styles.title}>Share to Mesa Robles</div>
            <div className={styles.subtitle}>{calc.recipeName || 'Untitled Recipe'}</div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {nothingToShare ? (
          <div className={styles.note}>
            Add some ingredients first — a shared recipe needs a formula.
          </div>
        ) : (
          <>
            {result.errors.length > 0 && (
              <div className={styles.errors}>
                <div className={styles.errTitle}>This file cannot be shared yet</div>
                {result.errors.map((e, i) => <div key={i} className={styles.errItem}>{e}</div>)}
              </div>
            )}

            {result.warnings.length > 0 && (
              <div className={styles.warnings}>
                <div className={styles.warnTitle}>Worth reading before you publish</div>
                {result.warnings.map((w, i) => <div key={i} className={styles.warnItem}>{w}</div>)}
              </div>
            )}

            <div className={styles.note}>
              This is exactly what the Mesa Robles page will show. Your ingredient
              prices and pricing settings are never included.
            </div>

            {pkg && <SharePreview display={pkg.display} />}
          </>
        )}

        <div className={styles.footer}>
          <a className={styles.helpLink} href={HELP_URL} onClick={(e) => {
            // Same tab, same app: let the hash open the help section.
            e.preventDefault();
            window.location.hash = 'share-help';
          }}>How sharing works</a>
          <div className={styles.footerRight}>
            {pkg && !nothingToShare && (
              <span className={styles.filename}>
                <span className={styles.filenameValue}>{filename}</span>
              </span>
            )}
            <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
            <button
              className={`${styles.btnPrimary} ${done ? styles.btnDone : ''}`}
              onClick={handleDownload}
              disabled={nothingToShare || !result.ok}
            >
              {done ? '✓ Downloaded!' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
